import React, { useEffect, useRef, useState } from "react";
import { ApiDataAccess } from "../../../common/ApiDataAccess";
import { ArticleLockButton } from "./ArticleLockButton";
import { ArticleShareButton } from "./ArticleShareButton";
import { useNavigate, useParams } from "react-router-dom";
import { useModal } from "@ebay/nice-modal-react";
import { ArticleToolbar } from "./ArticleToolbar";
import { TagsDisplayControl } from "../../home/controls/TagsDisplayControl";
import Confirmation from "../../home/controls/Confirmation";
import DownloadTokensModal from "./DownloadTokensModal";
import { toast } from "react-toastify";
import { ToastNotification } from "../../../../core/ToastNotification";
import MDEditor from "@uiw/react-md-editor";
import { useLoader } from "../../../../core/Loader";
import useNotifications from "../../../../core/Notifications";

import "../ArticleEditView.css";

export const ArticleDisplayControl = (props) => {

    const dataAccess = new ApiDataAccess("/api/articles");
    const previewDataAccess = new ApiDataAccess("/api/articles");

    const navigate = useNavigate();
    const confirmation = useModal(Confirmation);
    const downloadTokensModal = useModal(DownloadTokensModal);
    const { articleId, articleName } = useParams();
    const notifications = useNotifications();

    const loader = useLoader();

    const [article, setArticle] = useState({
        id: null,
        shareableUrl: null,
        title: null,
        content: null,
        author: null,
        createDate: null,
        tags: null,
        rawContent: null,
        isLocked: false,
        views: 0,
        userName: "",
        roleId: 0,
    });

    const [viewMode, setViewMode] = useState("plain");
    const [pdfPreview, setPdfPreview] = useState(null);
    const [loadingPdfPreview, setLoadingPdfPreview] = useState(false);
    const pdfPreviewFrameRef = useRef(null);

    useEffect(() => {
        console.log("useEffect loadArticle by type", props.type);
        loader.start();
        if (props.type === "id") {
            console.log("loading by id");
            loadArticleById(articleId);
        } else {
            loadArticleByUrl(articleName);
        }
    }, []);

    const loadArticleById = (id) => {
        var url = "/" + id + "/html";
        dataAccess.get(
            url,
            (data) => {
                setArticle({
                    id: data.id,
                    shareableUrl: data.shareableUrl,
                    title: data.name,
                    content: data.html,
                    author: data.author,
                    createDate: data.date,
                    tags: data.tags,
                    rawContent: data.rawContent,
                    isLocked: data.isLocked,
                    views: data.views,
                    currentUserName: data.currentUserName,
                    currentUserRole: data.currentUserRole,
                });
                console.log("article load by id", data);
                loader.stop();
            },
            (error) => {
                loader.stop();
            }
        );
    };

    const loadArticleByUrl = (url) => {
        url = "/url/" + url + "/html";
        dataAccess.get(
            url,
            (data) => {
                setArticle({
                    id: data.id,
                    shareableUrl: data.shareableUrl,
                    title: data.name,
                    content: data.html,
                    author: data.author,
                    createDate: data.date,
                    tags: data.tags,
                    rawContent: data.rawContent,
                    isLocked: data.isLocked,
                    views: data.views,
                    currentUserName: data.currentUserName,
                    currentUserRole: data.currentUserRole,
                    article: data,
                });
                loader.stop();
            },
            (error) => {
                loader.stop();
            }
        );
    };

    const loadPdfPreview = (id) => {
        setLoadingPdfPreview(true);
        previewDataAccess.get(
            "/" + id + "/pdf-preview",
            (data) => {
                setPdfPreview(data);
                setLoadingPdfPreview(false);
            },
            (error) => {
                setLoadingPdfPreview(false);
            }
        );
    };

    const onViewModeChange = (mode) => {
        setViewMode(mode);
        if (mode === "pdf" && pdfPreview === null && !loadingPdfPreview) {
            loadPdfPreview(article.id);
        }
    };

    const onPdfPreviewFrameLoad = () => {
        var frame = pdfPreviewFrameRef.current;
        if (frame && frame.contentWindow && frame.contentWindow.document.body) {
            frame.style.height = frame.contentWindow.document.body.scrollHeight + "px";
        }
    };

    const getPdfPreviewDoc = () => {

        // In PDF preview we don't want auto-generated captions ("Figure 1: ...") to appear, so strip caption blocks from the HTML used only for preview.
        var sanitizedPdfHtml = (pdfPreview.html || "")
            .replace(/<div class='caption'>[\s\S]*?<\/div>/gi, "")
            .replace(/<span class='caption'>[\s\S]*?<\/span>/gi, "");

        var logoHtml = pdfPreview.logoDataUri
            ? `<img class="pdf-preview-logo" src="${pdfPreview.logoDataUri}" alt="Logo" />`
            : "";
        var borderHtml = pdfPreview.borderDataUri
            ? `<img class="pdf-preview-border" src="${pdfPreview.borderDataUri}" alt="" />`
            : "";

        return `<html>
            <head>
                <style>
                    body { margin: 0; font-family: Arial, sans-serif; }
                    .pdf-preview-header { padding: 20px 30px 10px 30px; position: absolute }
                    .pdf-preview-logo { display: block; max-height: 60px; }
                    .pdf-preview-title { font-size: 22px; font-weight: bold; margin-top: 10px; }
                    .pdf-preview-border { width: 100%; display: block; height: 10px; object-fit: cover; }
                    .pdf-preview-body { padding: 0 30px; }
                    .pdf-preview-footer {
                        text-align: center;
                        font-size: 12px;
                        padding: 10px 30px 20px 30px;
                    }
                    ${pdfPreview.css || ""}
                </style>
            </head>
            <body>
                ${borderHtml}
                <div class="pdf-preview-header">
                    ${logoHtml}
                </div>
                <div class="pdf-preview-body">${sanitizedPdfHtml}</div>
            </body>
        </html>`;
    };

    const getFormattedDate = (item) => {
        var date = new Date(Date.parse(item));
        return date.getMonth() + 1 + "/" + date.getDate() + "/" + date.getFullYear();
    };

    const onTagClick = (tag) => {
        navigate(`/tags/${tag.id}/articles`);
    };

    const onToggle = (on) => {
        lockArticle(on);
    };

    const lockArticle = async (on) => {
        var url = on === true ? "/" + article.id + "/lock" : "/" + article.id + "/unlock";

        console.log("lock/unlock?", url);
        var success = await dataAccess.putCallback(url, null);
        console.log(success);
        if (!success.ok) {
            toast.error(ToastNotification, {
                data: { content: `Article ${on === true ? "lock" : "unlock"} failed.` },
                hideProgressBar: true,
            });
        } else {
            toast.info(ToastNotification, {
                data: { content: `Article ${on === true ? "locked." : "unlocked."}` },
                hideProgressBar: true,
            });
            loadArticleById(article.id);
        }
    };

    const deleteArticle = async (id) => {
        var response = await confirmation.show({
            content: "Are you sure you that you want to delete this article?",
            title: "Delete Article",
        });

        if (response.isConfirmed) {
            var url = "/" + id;
            var success = await dataAccess.deleteAsync(url);
            if (success.ok) {
                notifications.info({
                    message: `Article was deleted.`,
                });
                navigate("/", {
                    replace: true,
                });
            } else {
                notifications.error({
                    message: "Could not delete the article. Please try again later.",
                });
            }
        }
    };

    const getTokenNames = (content) => {
        if (!content) {
            return [];
        }
        var matches = content.match(/\{(.*?)\}/g) || [];
        var names = matches.map((match) => match.slice(1, -1));
        return [...new Set(names)];
    };

    const downloadPdf = async () => {
        var tokenValues = {};
        var tokenNames = getTokenNames(article.rawContent);

        if (tokenNames.length > 0) {
            var result = await downloadTokensModal.show({
                tokens: tokenNames.map((name) => ({ id: name, name })),
            });
            if (!result.isConfirmed) {
                return;
            }
            Object.keys(result.values).forEach((name) => {
                tokenValues["{" + name + "}"] = result.values[name];
            });
        }

        var response = await previewDataAccess.postCallback("/" + article.id + "/pdf", { tokens: tokenValues });

        if (response.ok) {
            var blob = await response.blob();
            var blobUrl = window.URL.createObjectURL(blob);
            var link = document.createElement("a");
            link.href = blobUrl;
            link.download = `wiki_article_${article.id}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } else {
            notifications.error({
                message: "Could not generate the PDF. Please try again later.",
            });
        }
    };

    const onClickPublish = async (id) => {
        var url = "/" + id + "/publish";

        var confirmationResult = await confirmation.show({
            content: "Are you sure you that you want to publish this article to a different environment?",
            title: "Publish Article",
        });

        if (confirmationResult.isConfirmed) {
            var publishResult = await dataAccess.postCallback(url, article);

            if (publishResult.ok) {
                notifications.info({
                    message: `Article was published successfully.`,
                });
            } else {
                notifications.error({
                    message: "Could not publish the article. Please try again later.",
                });
            }
        }
    };

    return (
        <div className="view">
            {article && article.title && (
                <div>
                    {!article.isLocked && (
                        <div
                            className="btn-round fixed-right background-green"
                            onClick={() => {
                                navigate(`/articles/${article.id}/edit`);
                            }}
                        >
                            <span className="glyphicon glyphicon-pencil"></span>
                        </div>
                    )}

                    {props.userRole === 4 && <ArticleLockButton locked={article.isLocked} onToggle={onToggle} />}

                    <h1>{article.title}</h1>
                    <ArticleToolbar
                        isLocked={article.isLocked}
                        onClickPublish={() => onClickPublish(article.id)}
                        articleId={article.id}
                        onLockClick={() => lockArticle(!article.isLocked)}
                        onDeleteClick={() => deleteArticle(article.id)}
                        onDownloadClick={downloadPdf}
                        viewMode={viewMode}
                        onViewModeChange={onViewModeChange}
                    />

                    <div>
                        <ArticleShareButton data={article.shareableUrl} />
                        <p>
                            by {article.author} on {getFormattedDate(article.createDate)} -{" "}
                            <span className="glyphicon glyphicon-user"></span> {article.views}
                        </p>
                        <TagsDisplayControl tags={article.tags} onTagClick={onTagClick} />
                    </div>
                    {viewMode === "pdf" ? (
                        loadingPdfPreview || pdfPreview === null ? (
                            <div id="content" className="article-content">
                                Loading PDF preview...
                            </div>
                        ) : (
                            <iframe
                                id="content"
                                title="PDF style preview"
                                ref={pdfPreviewFrameRef}
                                onLoad={onPdfPreviewFrameLoad}
                                className="pdf-preview-frame"
                                srcDoc={getPdfPreviewDoc()}
                            />
                        )
                    ) : (
                        <div id="content" className="markdown-editor" data-color-mode="light">
                            <MDEditor.Markdown source={article.content} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
